import { onScopeDispose, ref, watch } from 'vue'

export function useRafInterval(fn: (delta: number) => void, intervalMs: MaybeRef<number> = 1000) {
  const isActive = ref(true)
  const deltaTime = ref(0)

  // Handle both ref and raw number values
  const ms = ref(typeof intervalMs === 'number' ? intervalMs : intervalMs.value)

  let lastTime = performance.now()
  let rafId: number | undefined

  const loop = (timestamp: number) => {
    if (!isActive.value) {
      return
    }

    // Calculate time passed since last execution
    const delta = timestamp - lastTime

    // If enough time has passed, execute the callback
    if (delta >= ms.value) {
      fn(delta)
      deltaTime.value = delta
      lastTime = timestamp
    }

    // Schedule the next frame
    rafId = requestAnimationFrame(loop)
  }

  // If intervalMs is a ref, watch for changes
  if (typeof intervalMs !== 'number') {
    watch(
      intervalMs,
      (newVal) => {
        ms.value = newVal
      },
    )
  }

  const pause = () => {
    isActive.value = false
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = undefined
    }
  }

  const resume = () => {
    if (!isActive.value) {
      isActive.value = true
      lastTime = performance.now()
      rafId = requestAnimationFrame(loop)
    }
  }

  // Automatically clean up when component is unmounted
  onScopeDispose(
    () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
        rafId = undefined
      }
    },
    true,
  )

  // Start the loop on client-side
  if (import.meta.client) {
    rafId = requestAnimationFrame(loop)
  }

  return {
    deltaTime,
    isActive,
    pause,
    resume,
  }
}

export function useRafTimeout(fn: () => void, ms: number = 1000, { immediate = true }: { immediate?: boolean } = {}) {
  const isActive = ref(false)
  const isPending = ref(false)
  const remainingTime = ref(ms)

  let rafId: number | undefined
  let startTime = 0

  const stop = () => {
    isActive.value = false
    isPending.value = false

    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = undefined
    }
  }

  const start = () => {
    // Don't start if already active
    if (isActive.value || isPending.value) {
      return
    }

    isActive.value = true
    isPending.value = true
    startTime = performance.now()

    const loop = (timestamp: number) => {
      if (!isActive.value) {
        // If stopped, update remaining time for potential resume
        remainingTime.value = ms - (timestamp - startTime)

        return
      }

      const elapsed = timestamp - startTime

      if (elapsed >= ms) {
        // Time reached - execute callback and cleanup
        isPending.value = false
        isActive.value = false
        remainingTime.value = 0
        fn()
      }
      else {
        // Not yet - update remaining time and continue
        remainingTime.value = ms - elapsed
        rafId = requestAnimationFrame(loop)
      }
    }

    // Start the loop
    rafId = requestAnimationFrame(loop)
  }

  const restart = () => {
    stop()
    start()
  }

  // Start immediately if requested
  if (immediate && import.meta.client) {
    start()
  }

  // Automatically clean up when component is unmounted
  onScopeDispose(stop, true)

  return {
    isActive,
    isPending,
    remainingTime,
    restart,
    start,
    stop,
  }
}
